import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Star, 
  Building2, 
  Users, 
  Clock, 
  TrendingUp,
  BarChart3,
  RefreshCw,
  ArrowLeft,
  FileText,
  Award
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import './TableroGeneral.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORES_ESTRELLAS = ['#F44336', '#FF9800', '#FFC107', '#8BC34A', '#4CAF50'];
const PREGUNTAS_LABELS = {
  dentro_plazo: 'Dentro del plazo',
  averiguacion_caso: 'Averiguación del caso',
  respuesta_tecnica: 'Respuesta técnica',
  producto_coherente: 'Producto coherente',
  actuacion_objetiva: 'Actuación objetiva'
};

const TableroCalificaciones = ({ onBack }) => {
  const [loading, setLoading] = useState(true);
  const [datos, setDatos] = useState(null);
  const [checklistMetrics, setChecklistMetrics] = useState(null);
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    fetchDatos();
    fetchChecklistMetrics();
  }, []);

  const fetchDatos = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/calificaciones/tablero`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDatos(response.data);
    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChecklistMetrics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/calificaciones/metricas-checklist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChecklistMetrics(response.data);
    } catch (error) {
      console.error('Error al cargar métricas de checklist:', error);
    }
  };

  const renderStars = (promedio) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} style={{ color: i <= Math.round(promedio) ? '#FFB800' : '#E0E0E0', fontSize: '1.25rem' }}>
          ★
        </span>
      );
    }
    return stars;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>Cargando tablero de calificaciones...</p>
      </div>
    );
  }

  if (!datos) {
    return (
      <div className="tablero-general-container">
        <button className="btn btn-secondary" onClick={onBack}>
          <ArrowLeft size={20} /> Volver
        </button>
        <p style={{ textAlign: 'center', padding: '2rem' }}>No hay datos disponibles</p>
      </div>
    );
  }

  // Preparar datos para gráfico de distribución
  const dataDistribucion = [1, 2, 3, 4, 5].map(estrella => {
    const found = datos.distribucion?.find(d => d._id === estrella);
    return {
      name: `${estrella} ★`,
      value: found?.count || 0,
      estrellas: estrella
    };
  });

  // Preparar datos para gráfico de instituciones
  const dataInstituciones = (datos.por_institucion || []).slice(0, 8).map(inst => ({
    name: inst._id?.length > 20 ? inst._id.substring(0, 20) + '...' : inst._id,
    fullName: inst._id,
    promedio: parseFloat(inst.promedio?.toFixed(2) || 0),
    calificaciones: inst.total_calificaciones,
    tiempo: Math.round(inst.tiempo_promedio_dias || 0)
  }));

  return (
    <div className="tablero-general-container">
      {/* Header */}
      <div className="tablero-header">
        <div>
          <button className="btn btn-secondary btn-sm" onClick={onBack} style={{ marginBottom: '0.5rem' }}>
            <ArrowLeft size={16} /> Volver a Administración
          </button>
          <h1>
            <Award size={32} />
            Tablero de Calificaciones
          </h1>
          <p>Evaluación de Instancias de Probidad</p>
        </div>
        <button className="btn btn-primary" onClick={() => { fetchDatos(); fetchChecklistMetrics(); }}>
          <RefreshCw size={20} />
          Actualizar
        </button>
      </div>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '0.5rem', 
        marginBottom: '1.5rem',
        borderBottom: '2px solid #E0E0E0',
        paddingBottom: '0'
      }} data-testid="tablero-tabs">
        <button
          onClick={() => setActiveTab('general')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            background: activeTab === 'general' ? '#1565C0' : 'transparent',
            color: activeTab === 'general' ? 'white' : '#666',
            fontWeight: 600,
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontSize: '0.9rem',
            transition: 'all 0.2s ease'
          }}
          data-testid="tab-general"
        >
          <BarChart3 size={16} style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />
          Calificaciones
        </button>
        <button
          onClick={() => setActiveTab('checklist')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            background: activeTab === 'checklist' ? '#1565C0' : 'transparent',
            color: activeTab === 'checklist' ? 'white' : '#666',
            fontWeight: 600,
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontSize: '0.9rem',
            transition: 'all 0.2s ease'
          }}
          data-testid="tab-checklist"
        >
          <TrendingUp size={16} style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />
          Métricas de Evaluación
        </button>
      </div>

      {activeTab === 'general' && (
      <>
      {/* KPIs Principales */}
      <div className="kpi-grid">
        <div className="kpi-card kpi-primary">
          <div className="kpi-icon">
            <Star size={32} />
          </div>
          <div className="kpi-content">
            <div className="kpi-value">{datos.promedio_general?.toFixed(1) || '0.0'}</div>
            <div className="kpi-label">Calificación Promedio</div>
            <div style={{ marginTop: '0.25rem' }}>{renderStars(datos.promedio_general || 0)}</div>
          </div>
        </div>

        <div className="kpi-card kpi-success">
          <div className="kpi-icon">
            <FileText size={32} />
          </div>
          <div className="kpi-content">
            <div className="kpi-value">{datos.total_calificaciones || 0}</div>
            <div className="kpi-label">Total Calificaciones</div>
          </div>
        </div>

        <div className="kpi-card kpi-info">
          <div className="kpi-icon">
            <Clock size={32} />
          </div>
          <div className="kpi-content">
            <div className="kpi-value">{Math.round(datos.tiempo_promedio_general || 0)}</div>
            <div className="kpi-label">Días Promedio de Gestión</div>
          </div>
        </div>

        <div className="kpi-card kpi-warning">
          <div className="kpi-icon">
            <Building2 size={32} />
          </div>
          <div className="kpi-content">
            <div className="kpi-value">{datos.por_institucion?.length || 0}</div>
            <div className="kpi-label">Instituciones Evaluadas</div>
          </div>
        </div>
      </div>

      {/* Gráficos - Fila 1 */}
      <div className="charts-grid">
        {/* Distribución de Calificaciones */}
        <div className="chart-card">
          <h2>
            <Star size={20} style={{ marginRight: '0.5rem' }} />
            Distribución de Calificaciones
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dataDistribucion}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" name="Calificaciones" radius={[4, 4, 0, 0]}>
                {dataDistribucion.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORES_ESTRELLAS[entry.estrellas - 1]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Promedio por Institución */}
        <div className="chart-card">
          <h2>
            <Building2 size={20} style={{ marginRight: '0.5rem' }} />
            Calificación por Institución
          </h2>
          {dataInstituciones.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dataInstituciones} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 5]} />
                <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 11 }} />
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="custom-tooltip">
                          <p className="label"><strong>{data.fullName}</strong></p>
                          <p>Promedio: {data.promedio} ★</p>
                          <p>Calificaciones: {data.calificaciones}</p>
                          <p>Tiempo promedio: {data.tiempo} días</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="promedio" fill="#FFB800" name="Promedio" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#757575' }}>
              No hay datos de instituciones
            </div>
          )}
        </div>
      </div>

      {/* Tablas de Detalle */}
      <div className="charts-grid">
        {/* Ranking de Asesores de Probidad */}
        <div className="chart-card">
          <h2>
            <Users size={20} style={{ marginRight: '0.5rem' }} />
            Ranking de Asesores de Probidad
          </h2>
          <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #ddd' }}>#</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Asesor</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Promedio</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Eval.</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Días Prom.</th>
                </tr>
              </thead>
              <tbody>
                {(datos.por_asesor || []).slice(0, 10).map((asesor, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '0.75rem' }}>
                      <span style={{ 
                        background: idx < 3 ? ['#FFD700', '#C0C0C0', '#CD7F32'][idx] : '#E0E0E0',
                        color: idx < 3 ? '#000' : '#666',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '50%',
                        fontWeight: 600,
                        fontSize: '0.75rem'
                      }}>
                        {idx + 1}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <strong>{asesor._id}</strong>
                      <br />
                      <small style={{ color: '#666' }}>{asesor.institucion}</small>
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      <span style={{ color: '#FFB800', marginRight: '0.25rem' }}>
                        {'★'.repeat(Math.round(asesor.promedio))}
                      </span>
                      <strong>{asesor.promedio?.toFixed(1)}</strong>
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>{asesor.total_calificaciones}</td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>{Math.round(asesor.tiempo_promedio_dias || 0)}</td>
                  </tr>
                ))}
                {(!datos.por_asesor || datos.por_asesor.length === 0) && (
                  <tr>
                    <td colSpan="5" style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                      No hay calificaciones registradas
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Por Tipo de Gestión */}
        <div className="chart-card">
          <h2>
            <BarChart3 size={20} style={{ marginRight: '0.5rem' }} />
            Por Tipo de Gestión
          </h2>
          <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
            {(datos.por_tipo_gestion || []).map((tipo, idx) => (
              <div key={idx} style={{ 
                padding: '1rem', 
                borderBottom: '1px solid #eee',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <strong>{tipo._id}</strong>
                  <br />
                  <small style={{ color: '#666' }}>{tipo.total} calificaciones</small>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#FFB800' }}>
                    {tipo.promedio?.toFixed(1)} ★
                  </div>
                  <small style={{ color: '#666' }}>
                    {Math.round(tipo.tiempo_promedio_dias || 0)} días prom.
                  </small>
                </div>
              </div>
            ))}
            {(!datos.por_tipo_gestion || datos.por_tipo_gestion.length === 0) && (
              <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                No hay datos por tipo de gestión
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Últimas Calificaciones */}
      <div className="chart-card" style={{ marginTop: '1.5rem' }}>
        <h2>
          <Clock size={20} style={{ marginRight: '0.5rem' }} />
          Últimas Calificaciones
        </h2>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Fecha</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Gestión</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Institución</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Asesor</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Producto</th>
                <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Calificación</th>
                <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Días</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Observación</th>
              </tr>
            </thead>
            <tbody>
              {(datos.ultimas_calificaciones || []).map((cal, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
                    {cal.createdAt ? new Date(cal.createdAt).toLocaleDateString('es-GT') : 'N/A'}
                  </td>
                  <td style={{ padding: '0.75rem' }}>
                    <strong>{cal.nomenclatura}</strong>
                    <br />
                    <small style={{ color: '#666' }}>{cal.tipo_gestion}</small>
                  </td>
                  <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>{cal.institucion}</td>
                  <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>{cal.asesor_probidad_nombre}</td>
                  <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
                    <span style={{ 
                      background: '#E8F5E9', 
                      color: '#2E7D32', 
                      padding: '0.25rem 0.5rem', 
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      fontWeight: 500
                    }}>
                      {cal.producto_obtenido || 'N/A'}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                    <span style={{ color: '#FFB800' }}>{'★'.repeat(cal.calificacion)}</span>
                    <span style={{ color: '#E0E0E0' }}>{'★'.repeat(5 - cal.calificacion)}</span>
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'center' }}>{cal.dias_gestion}</td>
                  <td style={{ padding: '0.75rem', fontSize: '0.875rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {cal.observacion || '-'}
                  </td>
                </tr>
              ))}
              {(!datos.ultimas_calificaciones || datos.ultimas_calificaciones.length === 0) && (
                <tr>
                  <td colSpan="8" style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                    No hay calificaciones registradas
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}

      {/* Tab: Métricas de Evaluación (Checklist) */}
      {activeTab === 'checklist' && (
        <div data-testid="checklist-metrics-section">
          {!checklistMetrics || checklistMetrics.total_evaluaciones === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '3rem', 
              background: '#F5F5F5', 
              borderRadius: '12px',
              color: '#666'
            }}>
              <BarChart3 size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
              <h3>Sin datos de evaluación con checklist</h3>
              <p>Las métricas aparecerán cuando se completen gestiones con el nuevo sistema de evaluación por checklist.</p>
            </div>
          ) : (
            <>
              {/* KPIs de Checklist */}
              <div className="kpi-grid">
                <div className="kpi-card kpi-primary">
                  <div className="kpi-icon"><FileText size={32} /></div>
                  <div className="kpi-content">
                    <div className="kpi-value">{checklistMetrics.total_evaluaciones}</div>
                    <div className="kpi-label">Evaluaciones con Checklist</div>
                  </div>
                </div>
                <div className="kpi-card kpi-success">
                  <div className="kpi-icon"><TrendingUp size={32} /></div>
                  <div className="kpi-content">
                    <div className="kpi-value">{checklistMetrics.overrides.porcentaje}%</div>
                    <div className="kpi-label">Ajustes Manuales</div>
                  </div>
                </div>
                <div className="kpi-card kpi-warning">
                  <div className="kpi-icon"><Building2 size={32} /></div>
                  <div className="kpi-content">
                    <div className="kpi-value">{checklistMetrics.por_institucion.length}</div>
                    <div className="kpi-label">Instituciones Evaluadas</div>
                  </div>
                </div>
              </div>

              {/* Cumplimiento por Pregunta */}
              <div style={{ 
                background: 'white', 
                borderRadius: '12px', 
                padding: '1.5rem', 
                marginBottom: '1.5rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}>
                <h3 style={{ margin: '0 0 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Award size={20} />
                  Cumplimiento por Pregunta del Checklist
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {Object.entries(checklistMetrics.por_pregunta).map(([key, data]) => (
                    <div key={key} data-testid={`metric-${key}`}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem', fontSize: '0.875rem' }}>
                        <span style={{ fontWeight: 500 }}>{PREGUNTAS_LABELS[key] || key}</span>
                        <span style={{ 
                          fontWeight: 600, 
                          color: data.porcentaje_cumplimiento >= 80 ? '#2E7D32' : data.porcentaje_cumplimiento >= 50 ? '#F57F17' : '#C62828'
                        }}>
                          {data.porcentaje_cumplimiento}% ({data.si}/{data.total})
                        </span>
                      </div>
                      <div style={{ height: '8px', background: '#E0E0E0', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{
                          height: '100%',
                          width: `${data.porcentaje_cumplimiento}%`,
                          background: data.porcentaje_cumplimiento >= 80 ? '#4CAF50' : data.porcentaje_cumplimiento >= 50 ? '#FFC107' : '#F44336',
                          borderRadius: '4px',
                          transition: 'width 0.5s ease'
                        }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Gráfico de barras por pregunta */}
              {(() => {
                const chartData = Object.entries(checklistMetrics.por_pregunta).map(([key, data]) => ({
                  name: PREGUNTAS_LABELS[key]?.substring(0, 15) || key,
                  cumplimiento: data.porcentaje_cumplimiento,
                  si: data.si,
                  no: data.no
                }));
                return (
                  <div style={{ 
                    background: 'white', 
                    borderRadius: '12px', 
                    padding: '1.5rem', 
                    marginBottom: '1.5rem',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                  }}>
                    <h3 style={{ margin: '0 0 1rem' }}>
                      <BarChart3 size={20} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
                      Cumplimiento del Checklist
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                        <YAxis type="category" dataKey="name" width={120} fontSize={12} />
                        <Tooltip formatter={(val) => `${val}%`} />
                        <Bar dataKey="cumplimiento" fill="#1565C0" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                );
              })()}

              {/* Desempeño por Institución */}
              {checklistMetrics.por_institucion.length > 0 && (
                <div style={{ 
                  background: 'white', 
                  borderRadius: '12px', 
                  padding: '1.5rem', 
                  marginBottom: '1.5rem',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}>
                  <h3 style={{ margin: '0 0 1rem' }}>
                    <Building2 size={20} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
                    Desempeño por Institución
                  </h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#F5F5F5' }}>
                          <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600 }}>Institución</th>
                          <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: 600 }}>Promedio</th>
                          <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: 600 }}>Evaluaciones</th>
                          {Object.keys(PREGUNTAS_LABELS).map(key => (
                            <th key={key} style={{ padding: '0.5rem', textAlign: 'center', fontWeight: 600, fontSize: '0.75rem' }}>
                              {PREGUNTAS_LABELS[key]}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {checklistMetrics.por_institucion.map((inst, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #EEE' }}>
                            <td style={{ padding: '0.75rem', fontWeight: 500 }}>{inst.institucion}</td>
                            <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                              <span style={{ color: '#FFB800', marginRight: '0.25rem' }}>★</span>
                              {inst.promedio_calificacion}
                            </td>
                            <td style={{ padding: '0.75rem', textAlign: 'center' }}>{inst.total_evaluaciones}</td>
                            {Object.keys(PREGUNTAS_LABELS).map(key => {
                              const pct = inst.cumplimiento_por_pregunta?.[key] || 0;
                              return (
                                <td key={key} style={{ padding: '0.5rem', textAlign: 'center' }}>
                                  <span style={{
                                    display: 'inline-block',
                                    padding: '0.2rem 0.5rem',
                                    borderRadius: '4px',
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    background: pct >= 80 ? '#E8F5E9' : pct >= 50 ? '#FFF8E1' : '#FFEBEE',
                                    color: pct >= 80 ? '#2E7D32' : pct >= 50 ? '#F57F17' : '#C62828'
                                  }}>
                                    {pct}%
                                  </span>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Tendencia Mensual */}
              {checklistMetrics.tendencia_mensual.length > 1 && (
                <div style={{ 
                  background: 'white', 
                  borderRadius: '12px', 
                  padding: '1.5rem',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}>
                  <h3 style={{ margin: '0 0 1rem' }}>
                    <TrendingUp size={20} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
                    Tendencia Mensual de Calificaciones
                  </h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={checklistMetrics.tendencia_mensual}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="mes" fontSize={12} />
                      <YAxis domain={[0, 5]} fontSize={12} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="promedio" stroke="#1565C0" strokeWidth={2} name="Promedio" dot={{ fill: '#1565C0' }} />
                      <Line type="monotone" dataKey="total" stroke="#4CAF50" strokeWidth={2} name="Evaluaciones" yAxisId={0} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TableroCalificaciones;
